import {
  KameleoonLogger,
  Milliseconds,
  Parser,
} from '@kameleoon/javascript-sdk-core';
import { CredentialsType } from './types';

export const SILENCE_PERIOD = 5 * Milliseconds.Minute;
export const TOKEN_EXPIRATION_GAP = Milliseconds.Minute;
export const TOKEN_OBSOLESCENCE_GAP = 30 * Milliseconds.Minute;

const BASIC_AUTHORIZATION_PREFIX = 'Basic ';

export type AccessTokenResponseType = {
  accessToken: string;
  expiresIn: number; // seconds
};

/**
 * Performs one access token request with the given `Authorization` header value;
 * must reject on any failure (network error, abort, non-2xx status, malformed body)
 */
export type FetchAccessTokenType = (
  basicAuthToken: string,
  signal: AbortSignal,
) => Promise<AccessTokenResponseType>;

/**
 * Caches the OAuth access token and refreshes it in the background before it expires.
 * Concurrent callers share one in-flight request, which is bounded by `requestTimeout`
 * independently of the callers. After a failed request the token is not requested for
 * `SILENCE_PERIOD`; callers get `null` and proceed without it.
 */
export class AccessTokenSource {
  private readonly basicAuthToken: string;
  private readonly fetchAccessToken: FetchAccessTokenType;
  private readonly requestTimeout: number;
  private cachedToken: ExpiringToken | null = null;
  private silentAfterFetchFailureUntil = 0;
  private fetching: Promise<string | null> | null = null;

  constructor(
    { clientId, clientSecret }: CredentialsType,
    fetchAccessToken: FetchAccessTokenType,
    requestTimeout: number,
  ) {
    this.basicAuthToken =
      BASIC_AUTHORIZATION_PREFIX + encodeBase64(`${clientId}:${clientSecret}`);
    this.fetchAccessToken = fetchAccessToken;
    this.requestTimeout = requestTimeout;
  }

  /**
   * @param signal - the caller's own signal; once it aborts the caller gets `null`
   * while the shared fetch carries on for the other waiters and the cache
   */
  public async getToken(signal: AbortSignal | null): Promise<string | null> {
    KameleoonLogger.debug('CALL: AccessTokenSource.getToken()');
    const now = Date.now();
    const token = this.cachedToken;
    const silent = now < this.silentAfterFetchFailureUntil;
    let result: string | null;

    if (token && !token.isExpired(now)) {
      if (token.isObsolete(now) && !silent) {
        this.fetchToken(); // refresh in background, keep serving the current token
      }
      result = token.value;
    } else if (silent) {
      result = null;
    } else {
      result = await this.waitForFetch(signal);
    }

    KameleoonLogger.debug('RETURN: AccessTokenSource.getToken()');
    return result;
  }

  public discardToken(token: string | null): void {
    KameleoonLogger.debug(
      () =>
        `CALL: AccessTokenSource.discardToken(token: ${Parser.parseSecret(
          token,
        )})`,
    );
    if (this.cachedToken?.value === token) {
      this.cachedToken = null;
    }
    KameleoonLogger.debug(
      () =>
        `RETURN: AccessTokenSource.discardToken(token: ${Parser.parseSecret(
          token,
        )})`,
    );
  }

  // Starts a fetch unless one is already in flight; returns the promise shared by all waiters
  private fetchToken(): Promise<string | null> {
    if (!this.fetching) {
      this.fetching = this.performFetch();
    }
    return this.fetching;
  }

  // Waits for the fetch until the caller's signal aborts
  private async waitForFetch(
    signal: AbortSignal | null,
  ): Promise<string | null> {
    const fetching = this.fetchToken();

    if (!signal) {
      return fetching;
    }

    const token = await Promise.race([fetching, whenAborted(signal)]);

    if (signal.aborted) {
      KameleoonLogger.warning(
        'Access token is not fetched within the request timeout; the request proceeds without it',
      );

      return null;
    }

    return token;
  }

  private async performFetch(): Promise<string | null> {
    // Own timeout: a background refresh outlives the request that triggered it
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.requestTimeout);

    try {
      const { accessToken, expiresIn } = await this.fetchAccessToken(
        this.basicAuthToken,
        controller.signal,
      );

      this.cachedToken = this.newExpiringToken(accessToken, expiresIn);
      KameleoonLogger.info('Fetched access token');

      return accessToken;
    } catch (error) {
      this.silentAfterFetchFailureUntil = Date.now() + SILENCE_PERIOD;
      KameleoonLogger.warning(
        () =>
          `Failed to fetch access token (${
            error instanceof Error ? error.message : error
          }); it will not be requested for ${
            SILENCE_PERIOD / Milliseconds.Second
          }s`,
      );

      return null;
    } finally {
      clearTimeout(timeoutId);
      // Cleared before waiters resume, so a waiter re-entering `getToken()` can start a new fetch
      this.fetching = null;
    }
  }

  private newExpiringToken(
    token: string,
    expiresInSeconds: number,
  ): ExpiringToken {
    const expiresIn = expiresInSeconds * Milliseconds.Second;
    const now = Date.now();
    const expirationTime = now + expiresIn - TOKEN_EXPIRATION_GAP;
    let obsolescenceTime: number;

    if (expiresIn > TOKEN_OBSOLESCENCE_GAP) {
      obsolescenceTime = now + expiresIn - TOKEN_OBSOLESCENCE_GAP;
    } else {
      obsolescenceTime = expirationTime;

      if (expiresIn <= TOKEN_EXPIRATION_GAP) {
        KameleoonLogger.error(
          `Access token life time (${expiresInSeconds}s) is not long enough to cache the token`,
        );
      } else {
        KameleoonLogger.warning(
          `Access token life time (${expiresInSeconds}s) is not long enough to refresh cached token in background`,
        );
      }
    }

    return new ExpiringToken(token, expirationTime, obsolescenceTime);
  }
}

export class ExpiringToken {
  constructor(
    public readonly value: string,
    public readonly expirationTime: number,
    public readonly obsolescenceTime: number,
  ) {}

  public isExpired(now: number): boolean {
    return now >= this.expirationTime;
  }

  public isObsolete(now: number): boolean {
    return now >= this.obsolescenceTime;
  }
}

function encodeBase64(value: string): string {
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(value).toString('base64');
  }

  const bytes = new TextEncoder().encode(value);
  return btoa(String.fromCodePoint(...bytes));
}

// Resolves with `null` once the signal aborts; the listener lives as long as the per-request signal
function whenAborted(signal: AbortSignal): Promise<null> {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve(null);
    } else {
      signal.addEventListener('abort', () => resolve(null), { once: true });
    }
  });
}
